import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, IconButton, Button, Chip, Tooltip,
  TextField, Collapse, CircularProgress, InputAdornment, Divider,
  ToggleButtonGroup, ToggleButton,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as ClockIcon,
  Event as DeadlineIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable
} from '@dnd-kit/core';
import { TYPE_COLORS as SHARED_TYPE_COLORS, FUNCTIE_COLORS } from '../../utils/taakKleuren';
import { getMonday } from '../../utils/weekUtils';
import { getCapacityCellStyle } from '../../utils/capacityColors';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { leesJson, meldFout } from '../../utils/apiFout';
import PageHeader from '../../ui-components/PageHeader';
import AfgerondSignaal from '../../ui-components/AfgerondSignaal';
import WeekSelector from '../../ui-components/WeekSelector';
import { computeDayLoad, weekAveragePct, pctOfDay, effectiveTaskHours, DEFAULT_CAPACITY_RULES } from '../../utils/capacityCalc';
import { DAGSTAAT_STIJL } from '../../utils/capacityColors';
import { useCapacityRules } from '../../utils/useCapacityRules';
import CapaciteitsRegels from '../../ui-components/CapaciteitsRegels';

// ─── Constants ───────────────────────────────────────────────────────────────

const DAY_NAMES = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
const MONTH_NAMES = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
// Dagbreedte hangt af van het venster (1/2/4 weken): minder weken = bredere,
// beter sleepbare kolommen (Joris, 1 sep).
const DAY_WIDTH_BY_WEEKS = { 1: 150, 2: 84, 4: 44, 5: 44 };
const DAY_WIDTH = 44; // fallback voor plekken zonder prop
const SIDEBAR_WIDTH = 180;

// Gedeelde taak-kleuren + functie-tinten (Editor kleurt als Montage, enz.).
const TYPE_COLORS = { ...SHARED_TYPE_COLORS, ...FUNCTIE_COLORS };

const FUNCTION_GROUPS = [
  { key: 'Editor', label: 'Editors', keywords: ['editor', 'montage', 'colorgrad'] },
  { key: 'Animator', label: 'Animators', keywords: ['animator', 'animatie', 'motion'] },
  { key: 'Illustrator', label: 'Illustrators', keywords: ['illustrat'] },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────


function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addWorkDays(date, days) {
  const d = new Date(date);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) added++;
  }
  return d;
}

function subWorkDays(date, days) {
  const d = new Date(date);
  let removed = 0;
  while (removed < days) {
    d.setDate(d.getDate() - 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) removed++;
  }
  return d;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Weekenddagen zijn geen werkdagen — schuif door naar de eerstvolgende maandag. */
function nextWorkDay(date) {
  const d = startOfDay(date);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d;
}

function werkdagenVoor(task) {
  // Zelfde uren-schatter als de capaciteitsberekening (effectiveTaskHours) —
  // geen tweede definitie van "hoe zwaar telt een taak zonder uren".
  const { val } = effectiveTaskHours(task, DEFAULT_CAPACITY_RULES);
  return Math.max(1, Math.ceil(val / 8));
}

function eindDatumVoor(start, dagen) {
  return dagen > 1 ? addWorkDays(start, dagen - 1) : new Date(start);
}

/**
 * Plant een taak in vanaf de gewenste startdag, maar respecteert de deadline:
 * loopt de taak eroverheen dan schuift de start naar voren tot het einde precies
 * op de deadline valt. Kan dat niet meer (deadline te dichtbij), dan blijft de
 * gewenste start staan en geeft `waarschuwing` aan wat er misgaat.
 */
function planTaak(task, gewensteStart) {
  const dagen = werkdagenVoor(task);
  const vandaag = nextWorkDay(new Date());
  let start = nextWorkDay(Math.max(startOfDay(gewensteStart).getTime(), vandaag.getTime()));
  let eind = eindDatumVoor(start, dagen);
  let waarschuwing = null;
  let verschoven = false;

  const deadline = task.deadline ? startOfDay(new Date(task.deadline)) : null;
  if (deadline && !isNaN(deadline) && eind > deadline) {
    const laatsteStart = nextWorkDay(subWorkDays(deadline, dagen - 1));
    if (eindDatumVoor(laatsteStart, dagen) <= deadline && laatsteStart >= vandaag) {
      start = laatsteStart;
      eind = eindDatumVoor(start, dagen);
      verschoven = true;
    } else {
      waarschuwing = `Past niet meer vóór de deadline (${formatISO(deadline)}) — ${dagen} werkdag(en) nodig.`;
    }
  }

  return { start, eind, waarschuwing, verschoven };
}

function formatISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isToday(date) {
  const t = new Date();
  return date.getFullYear() === t.getFullYear() &&
    date.getMonth() === t.getMonth() &&
    date.getDate() === t.getDate();
}

function daysUntil(dateStr) {
  if (!dateStr) return Infinity;
  const target = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

function generateWorkingDays(startDate, numWeeks = 5) {
  const days = [];
  let current = new Date(startDate);
  const endDate = addDays(startDate, numWeeks * 7);

  // Exclusieve grens: met <= viel de maandag van de vólgende week ook in het
  // venster (6 werkdagen bij '1w' — Codex 1 sep, punt 15).
  while (current < endDate) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      days.push({
        date: new Date(current),
        iso: formatISO(current),
        dayName: DAY_NAMES[dayOfWeek],
        dayNum: current.getDate(),
        month: MONTH_NAMES[current.getMonth()],
        isFirstOfMonth: current.getDate() === 1 || days.length === 0,
      });
    }
    current = addDays(current, 1);
  }
  return days;
}


function getPersonFunctionGroup(person) {
  // Handle function being array or string
  let func = person.function;
  if (Array.isArray(func)) func = func.join(' ');
  func = (func || '').toLowerCase();

  for (const group of FUNCTION_GROUPS) {
    if (group.keywords.some(kw => func.includes(kw))) {
      return group.key;
    }
  }
  return 'Overig';
}

function getTaskTypeGroup(task) {
  // Handle type being array or string
  let type = task.type;
  if (Array.isArray(type)) type = type.join(' ');
  type = (type || '').toLowerCase();

  if (type.includes('montage') || type.includes('editor') || type.includes('colorgrad') || type.includes('sound')) {
    return 'Editor';
  }
  if (type.includes('animat') || type.includes('motion')) {
    return 'Animator';
  }
  if (type.includes('illustrat')) {
    return 'Illustrator';
  }
  return 'Overig';
}

// ─── Draggable Task Bar ──────────────────────────────────────────────────────

function DraggableTaskBar({ task, dayWidth, totalDays, workingDays, left = null, top = 0, ongepland = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `task-${task.id}`,
    data: { type: 'task-bar', task }
  });

  const typeColor = TYPE_COLORS[task.type] || '#6b7280';
  const daysToDeadline = daysUntil(task.deadline);

  // Calculate bar width based on hours (8h = 1 day width)
  const taskDays = werkdagenVoor(task);
  const barWidth = taskDays * dayWidth;

  // Absoluut positioneren zodra we een kolom weten; anders meelopen in de flow.
  const positie = left === null ? {} : { position: 'absolute', left, top };

  const style = transform ? {
    transform: `translate(${transform.x}px, ${transform.y}px)`,
    zIndex: 1000,
  } : {};

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" fontWeight={600}>{task.name}</Typography>
          <Typography variant="caption" display="block">{task.hours}u / {taskDays}d</Typography>
          <Typography variant="caption" display="block">
            {task.startDate ? `Gepland vanaf ${task.startDate}` : 'Nog geen startdatum'}
          </Typography>
          {task.deadline && (
            <Typography variant="caption" display="block" sx={{ color: daysToDeadline <= 3 ? '#fca5a5' : '#a5b4fc' }}>
              Deadline: {daysToDeadline <= 0 ? 'Verlopen!' : `${daysToDeadline} dagen`}
            </Typography>
          )}
        </Box>
      }
      arrow
    >
      <Box
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 24,
          minWidth: barWidth,
          maxWidth: barWidth,
          px: 0.5,
          mr: 0.5,
          borderRadius: 1,
          backgroundColor: typeColor,
          color: '#fff',
          cursor: 'grab',
          opacity: isDragging ? 0.5 : 1,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'opacity 0.15s',
          // Zonder startdatum: gestreept, zodat "nog te plannen" zichtbaar verschilt
          // van "staat al op een datum".
          ...(ongepland && {
            backgroundImage: `repeating-linear-gradient(45deg, rgba(255,255,255,0.22) 0 4px, transparent 4px 8px)`,
          }),
          '&:hover': { boxShadow: '0 2px 6px rgba(0,0,0,0.3)' },
          '&:active': { cursor: 'grabbing' },
          ...positie,
          ...style
        }}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1 }}
        >
          {task.name}
        </Typography>
      </Box>
    </Tooltip>
  );
}

// ─── Droppable Capacity Cell ─────────────────────────────────────────────────

function DroppableCapacityCell({ personId, dateStr, capacity, isOver, staat, dayWidth = DAY_WIDTH }) {
  const stijl = DAGSTAAT_STIJL[staat];
  const { setNodeRef } = useDroppable({
    id: `cell-${personId}-${dateStr}`,
    data: { type: 'capacity-cell', personId, date: dateStr },
    // Op een vrije dag is iemand er niet: dan mag je er ook geen taak op laten vallen.
    // De cel accepteert de drop simpelweg niet meer i.p.v. hem stil in te plannen.
    disabled: staat === 'vrij',
  });

  const capColors = getCapacityCellStyle(capacity);

  return (
    <Box
      ref={setNodeRef}
      title={stijl?.titel}
      sx={{
        width: dayWidth,
        minWidth: dayWidth,
        height: 28,
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isOver ? '#bfdbfe' : (stijl ? stijl.bg : capColors.bg),
        transition: 'background-color 0.15s',
      }}
    >
      {(stijl || capacity > 0) && (
        <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 600, color: stijl ? stijl.kleur : capColors.text }}>
          {stijl ? stijl.tekst : `${Math.round(capacity)}%`}
        </Typography>
      )}
    </Box>
  );
}

// ─── Timeline Header ─────────────────────────────────────────────────────────

function TimelineHeader({ workingDays, dayWidth = DAY_WIDTH }) {
  return (
    <Box sx={{ display: 'flex', borderBottom: '2px solid #e5e7eb', bgcolor: '#f8fafc' }}>
      <Box sx={{ width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH, p: 1, borderRight: '1px solid #e5e7eb', bgcolor: '#f1f5f9' }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary">TAKEN</Typography>
      </Box>
      <Box sx={{ display: 'flex' }}>
        {workingDays.map((day, idx) => {
          const today = isToday(day.date);
          return (
            <Box
              key={day.iso}
              sx={{
                width: dayWidth,
                minWidth: dayWidth,
                textAlign: 'center',
                py: 0.5,
                borderRight: '1px solid #f3f4f6',
                borderLeft: day.isFirstOfMonth && idx > 0 ? '2px solid #3b82f6' : 'none',
                bgcolor: today ? '#eef2ff' : 'transparent',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: 10, color: today ? '#6366f1' : '#64748b', fontWeight: today ? 700 : 400 }}>
                {day.dayName}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 11, fontWeight: today ? 700 : 500, color: today ? '#6366f1' : 'inherit' }}>
                {day.dayNum}
              </Typography>
              {day.isFirstOfMonth && (
                <Typography variant="caption" sx={{ fontSize: 8, color: '#94a3b8' }}>{day.month}</Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Task Type Row (bars for one type within a project) ──────────────────────

const BAR_HEIGHT = 26;

/**
 * Zet elke taak op de kolom van zijn startdatum (StartDateUitvoerder) en verdeelt
 * overlappende taken over "lanes" zodat ze niet op elkaar vallen. Taken zonder
 * startdatum (of buiten het zichtbare venster) blijven links staan.
 */
function layoutTaakBalken(tasks, workingDays) {
  const idxPerISO = new Map(workingDays.map((d, i) => [d.iso, i]));
  const eersteISO = workingDays[0]?.iso;
  const laatsteISO = workingDays[workingDays.length - 1]?.iso;

  const kolomVoor = (iso) => {
    if (!iso || !eersteISO) return null;
    const dag = String(iso).split('T')[0];
    if (idxPerISO.has(dag)) return idxPerISO.get(dag);
    if (dag < eersteISO) return 0;                 // begon vóór dit venster
    if (dag > laatsteISO) return null;             // valt buiten beeld
    // Weekenddag: pak de eerstvolgende zichtbare werkdag.
    const volgende = workingDays.findIndex(d => d.iso > dag);
    return volgende === -1 ? null : volgende;
  };

  let pakLinks = 0;  // pack-positie voor taken zonder datum
  const items = tasks.map(task => {
    const breedte = werkdagenVoor(task);
    const kolom = kolomVoor(task.startDate);
    if (kolom === null) {
      const links = pakLinks;
      pakLinks += breedte;
      return { task, kolom: links, breedte, ongepland: true };
    }
    return { task, kolom, breedte, ongepland: false };
  });

  // Lanes toewijzen op volgorde van kolom — anders kan een latere taak een lane
  // krijgen die al bezet blijkt.
  const lanes = []; // per lane: eerstvolgende vrije kolom
  const geplaatst = [...items]
    .sort((a, b) => a.kolom - b.kolom)
    .map(item => {
      let lane = lanes.findIndex(vrijVanaf => vrijVanaf <= item.kolom);
      if (lane === -1) { lane = lanes.length; lanes.push(0); }
      lanes[lane] = item.kolom + item.breedte;
      return { ...item, lane };
    });

  return { geplaatst, laneCount: Math.max(1, lanes.length) };
}

function TaskTypeRow({ type, tasks, workingDays, dayWidth = DAY_WIDTH }) {
  const typeColor = TYPE_COLORS[type] || '#6b7280';
  const { geplaatst, laneCount } = useMemo(
    () => layoutTaakBalken(tasks, workingDays),
    [tasks, workingDays]
  );

  return (
    <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e7ff', bgcolor: '#f8fafc' }}>
      <Box sx={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        p: 0.5,
        pl: 3,
        borderRight: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: typeColor }} />
        <Typography variant="caption" fontWeight={600} sx={{ color: typeColor, fontSize: 11 }}>
          {type}
        </Typography>
        <Chip label={tasks.length} size="small" sx={{ height: 16, fontSize: 9, ml: 'auto' }} />
      </Box>
      <Box sx={{
        flex: 1,
        position: 'relative',
        minHeight: laneCount * BAR_HEIGHT + 8,
        py: 0.5,
        // Dagkolommen als achtergrond, zodat je ziet op welke dag een balk staat.
        backgroundImage: `repeating-linear-gradient(to right, #e2e8f0 0 1px, transparent 1px ${dayWidth}px)`,
      }}>
        {geplaatst.map(({ task, kolom, lane, ongepland }) => (
          <DraggableTaskBar
            key={task.id}
            task={task}
            dayWidth={dayWidth}
            totalDays={workingDays.length}
            workingDays={workingDays}
            left={kolom * dayWidth}
            top={lane * BAR_HEIGHT + 4}
            ongepland={ongepland}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── Project Task Group ──────────────────────────────────────────────────────

function ProjectTaskGroup({ project, workingDays, expanded, onToggle, dayWidth = DAY_WIDTH }) {
  // Group tasks by type
  const tasksByType = useMemo(() => {
    const groups = {};
    project.tasks.forEach(task => {
      const type = task.type || 'Overig';
      if (!groups[type]) groups[type] = [];
      groups[type].push(task);
    });
    return groups;
  }, [project.tasks]);

  return (
    <Box sx={{ borderBottom: '1px solid #cbd5e1' }}>
      {/* Project header */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          bgcolor: '#e2e8f0',
          cursor: 'pointer',
          '&:hover': { bgcolor: '#cbd5e1' }
        }}
      >
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
          {project.projectName}
        </Typography>
        <Chip
          label={`${project.tasks.length} taken`}
          size="small"
          sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
        />
      </Box>

      {/* Type rows */}
      <Collapse in={expanded}>
        {Object.entries(tasksByType).map(([type, typeTasks]) => (
          <TaskTypeRow dayWidth={dayWidth}
            key={type}
            type={type}
            tasks={typeTasks}
            workingDays={workingDays}
          />
        ))}
      </Collapse>
    </Box>
  );
}

// ─── Function Group Header ───────────────────────────────────────────────────

function FunctionGroupHeader({ name, count, expanded, onToggle }) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 0.75,
        bgcolor: '#f1f5f9',
        borderBottom: '1px solid #e2e8f0',
        cursor: 'pointer',
        '&:hover': { bgcolor: '#e2e8f0' }
      }}
    >
      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      <Typography variant="caption" fontWeight={700} color="text.secondary">
        {name}
      </Typography>
      <Chip label={count} size="small" sx={{ height: 18, fontSize: 9 }} />
    </Box>
  );
}

// ─── Compact Person Row ──────────────────────────────────────────────────────

function CompactPersonRow({ person, workingDays, existingTasks, calHours, rules = DEFAULT_CAPACITY_RULES, overCell, selected, gedimd = false, onSelect, dayWidth = DAY_WIDTH, expandedRow = false, onToggleExpand }) {
  // Gedeelde capaciteitsberekening (identiek aan het Capaciteit-overzicht).
  const dayISOs = useMemo(() => workingDays.map(d => d.iso), [workingDays]);
  const { uren: capacityHours, staat: dagStaat } = useMemo(
    () => computeDayLoad(existingTasks || [], dayISOs, calHours || {}, rules),
    [existingTasks, dayISOs, calHours, rules]
  );
  const capacityPerDay = useMemo(() => {
    const o = {};
    dayISOs.forEach(iso => { o[iso] = pctOfDay(capacityHours[iso], rules.hoursPerDay); });
    return o;
  }, [capacityHours, dayISOs, rules]);
  // Vrije dagen tellen niet mee in het gemiddelde — anders lijkt een vakantieweek "rustig"
  // en krijgt iemand die er niet is er werk bij.
  const avgCapacity = useMemo(
    () => weekAveragePct(capacityHours, dayISOs, rules.hoursPerDay, dagStaat),
    [capacityHours, dayISOs, rules, dagStaat]
  );

  const capColor = getCapacityCellStyle(avgCapacity);
  const sleeptHierheen = overCell?.personId === person.id;

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex',
        borderBottom: '1px solid #f1f5f9',
        cursor: 'pointer',
        bgcolor: selected ? '#dbeafe' : 'transparent',
        opacity: gedimd ? 0.45 : 1, transition: 'opacity .15s',
        '&:hover': { bgcolor: selected ? '#dbeafe' : '#f8fafc', opacity: 1 }
      }}
    >
      {/* Person name */}
      <Box sx={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        p: 0.5,
        px: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        borderRight: '1px solid #e2e8f0',
        // Duidelijke hint náár wie je sleept (Raijmond, 24 aug): naamcel licht op.
        bgcolor: sleeptHierheen ? '#bfdbfe' : selected ? '#dbeafe' : '#fafafa',
        boxShadow: sleeptHierheen ? 'inset 3px 0 0 #2563eb' : 'none',
        transition: 'background-color 80ms',
      }}>
        {onToggleExpand && (
          <Box onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#94a3b8', '&:hover': { color: '#334155' } }}>
            {expandedRow ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
          </Box>
        )}
        <Typography variant="caption" fontWeight={sleeptHierheen ? 800 : 500} noWrap sx={{ flex: 1, cursor: onToggleExpand ? 'pointer' : 'inherit' }}
          title={onToggleExpand ? 'Klik voor de taken van deze persoon' : undefined}
          onClick={onToggleExpand ? (e) => { e.stopPropagation(); onToggleExpand(); } : undefined}>
          {sleeptHierheen ? '→ ' : ''}{person.firstName || person.name?.split(' ')[0]}
        </Typography>
        <Chip
          label={`${avgCapacity}%`}
          size="small"
          sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: capColor.bg, color: capColor.text }}
        />
      </Box>

      {/* Day cells */}
      {workingDays.map(day => {
        const isOverThisCell = overCell?.personId === person.id && overCell?.date === day.iso;
        return (
          <DroppableCapacityCell
            key={day.iso}
            personId={person.id}
            dateStr={day.iso}
            capacity={capacityPerDay[day.iso] || 0}
            staat={dagStaat[day.iso]}
            isOver={isOverThisCell}
            dayWidth={dayWidth}
          />
        );
      })}
    </Box>
  );
}

// Sleepbare deliverable-chip: sleep hem op een dag-cel van een persoon om de
// hele deliverable daar in te passen (opent de speeltuin-popup).
function DraggableDeliverableChip({ dlv, selected, onClick, tasks }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `dlv-${dlv.id}`,
    data: { type: 'deliverable', deliverable: dlv, tasks },
  });
  return (
    <Box ref={setNodeRef} {...attributes} {...listeners} sx={{ display: 'inline-flex', opacity: isDragging ? 0.5 : 1 }}>
      <Chip
        label={`${dlv.name} (${dlv.taskCount})`}
        size="small"
        onClick={onClick}
        variant={selected ? 'filled' : 'outlined'}
        color={selected ? 'secondary' : 'default'}
        sx={{ height: 22, fontSize: 10.5, maxWidth: 240, cursor: 'grab',
          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
      />
    </Box>
  );
}

// ─── Speeltuin: hele deliverable bij één persoon inpassen (Joris, 1 sep) ─────
// Sleep een deliverable-chip op een persoon: dit dialoog stelt een opeenvolgende
// planning voor en laat je schuiven (nieuwe én bestaande taken) tot de dagen
// rond de 100% zitten — live dezelfde capaciteitsberekening als het overzicht.

const volgendeWerkdagNa = (d) => nextWorkDay(addDays(startOfDay(d), 1));
const prevWorkDayVoor = (iso) => {
  let d = addDays(new Date(`${String(iso).slice(0, 10)}T12:00:00`), -1);
  while (d.getDay() === 0 || d.getDay() === 6) d = addDays(d, -1);
  return startOfDay(d);
};

function SpeeltuinDialog({ info, persons, existingTasks, calendarHours, rules, onClose, onKlaar }) {
  const person = persons.find((p) => p.id === info?.personId);
  const [busy, setBusy] = useState(false);

  // Conceptplanning: opeenvolgend vanaf de drop-dag.
  const [concept, setConcept] = useState(() => {
    const uit = [];
    let cursor = nextWorkDay(new Date(`${info.startDatum}T12:00:00`));
    (info.taken || []).forEach((t) => {
      const dagen = werkdagenVoor(t);
      const start = new Date(cursor);
      const eind = eindDatumVoor(start, dagen);
      uit.push({ task: t, start: formatISO(start), eind: formatISO(eind) });
      cursor = volgendeWerkdagNa(eind);
    });
    return uit;
  });
  // Verschuivingen van bestaande taken (id → {start, eind}).
  const [bestaandeShift, setBestaandeShift] = useState({});

  const bestaand = useMemo(() => (existingTasks[info.personId] || []).map((t) => (
    bestaandeShift[t.id] ? { ...t, startDate: bestaandeShift[t.id].start, endDate: bestaandeShift[t.id].eind } : t
  )), [existingTasks, info.personId, bestaandeShift]);

  // Venster: van de vroegste tot de laatste betrokken datum (min 2 weken).
  const dagen = useMemo(() => {
    const eerste = getMonday(new Date(`${info.startDatum}T12:00:00`));
    return generateWorkingDays(eerste, 4);
  }, [info.startDatum]);
  const dagISOs = useMemo(() => dagen.map((d) => d.iso), [dagen]);

  const alleTaken = useMemo(() => ([
    ...bestaand,
    ...concept.map(({ task, start, eind }) => ({ id: task.id, name: task.name, type: task.type, hours: task.hours, startDate: start, endDate: eind })),
  ]), [bestaand, concept]);

  const { uren, staat } = useMemo(
    () => computeDayLoad(alleTaken, dagISOs, calendarHours[person?.email] || {}, rules),
    [alleTaken, dagISOs, calendarHours, person, rules]
  );

  const schuifConcept = (idx, richting) => {
    setConcept((prev) => prev.map((c, i) => {
      if (idx !== null && i !== idx) return c;
      const start = richting > 0 ? volgendeWerkdagNa(new Date(`${c.start}T12:00:00`)) : prevWorkDayVoor(c.start);
      const dagenT = werkdagenVoor(c.task);
      return { ...c, start: formatISO(start), eind: formatISO(eindDatumVoor(start, dagenT)) };
    }));
  };
  const schuifBestaand = (t, richting) => {
    const s0 = new Date(`${String(t.startDate).slice(0, 10)}T12:00:00`);
    const e0 = new Date(`${String(t.endDate || t.startDate).slice(0, 10)}T12:00:00`);
    const s1 = richting > 0 ? volgendeWerkdagNa(s0) : prevWorkDayVoor(formatISO(s0));
    // Echte werkdagen tellen: kalenderdagen als werkdagen interpreteren rekte
    // een vr–ma-taak na verschuiven op tot donderdag (Codex 1 sep, punt 14).
    let dagenT = 0;
    for (let d = new Date(s0); d <= e0; d = addDays(d, 1)) {
      const dw = d.getDay();
      if (dw !== 0 && dw !== 6) dagenT += 1;
    }
    dagenT = Math.max(1, dagenT);
    setBestaandeShift((prev) => ({ ...prev, [t.id]: { start: formatISO(s1), eind: formatISO(eindDatumVoor(s1, Math.min(dagenT, 10))) } }));
  };

  const toepassen = async () => {
    setBusy(true);
    try {
      const calls = [
        ...concept.map(({ task, start, eind }) => authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Assignee: [info.personId], StartDateUitvoerder: start, EndDateUitvoerder: eind, NogInTePlannen: false }),
        })),
        ...Object.entries(bestaandeShift).map(([id, w]) => authFetch(`${BACKEND_URL}/Api/tasks/${id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ StartDateUitvoerder: w.start, EndDateUitvoerder: w.eind }),
        })),
      ];
      const uitkomsten = await Promise.all(calls.map((c) => c.then((r) => r.ok).catch(() => false)));
      const fouten = uitkomsten.filter((ok) => !ok).length;
      if (fouten) toast.error(`${fouten} taken konden niet worden opgeslagen`);
      else toast.success(`${concept.length} taken ingepland bij ${person?.firstName || person?.name}`);
      onKlaar();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        🧩 {info.deliverableName} → {person?.firstName || person?.name}
      </DialogTitle>
      <DialogContent dividers>
        {/* Capaciteitsstrook: live, inclusief het concept */}
        <Box sx={{ display: 'flex', gap: '2px', mb: 2, flexWrap: 'wrap' }}>
          {dagen.map((d) => {
            const pct = pctOfDay(uren[d.iso] || 0, rules.hoursPerDay);
            const stijl = getCapacityCellStyle(pct);
            const vrij = staat[d.iso] === 'vrij';
            return (
              <Box key={d.iso} sx={{ width: 40, textAlign: 'center', borderRadius: 0.5, py: 0.25,
                bgcolor: vrij ? '#e0f2fe' : stijl.bg }}>
                <Typography sx={{ fontSize: 8.5, color: '#64748b' }}>{d.dayName} {d.dayNum}</Typography>
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: vrij ? '#0369a1' : stijl.text }}>
                  {vrij ? 'Vrij' : `${pct}%`}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Nieuwe taken ({info.deliverableName})</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button size="small" onClick={() => schuifConcept(null, -1)} sx={{ textTransform: 'none', py: 0 }}>← alles 1 dag</Button>
          <Button size="small" onClick={() => schuifConcept(null, 1)} sx={{ textTransform: 'none', py: 0 }}>alles 1 dag →</Button>
        </Box>
        {concept.map((c, i) => (
          <Box key={c.task.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
            <Typography noWrap sx={{ fontSize: 12.5, flex: 1, minWidth: 0 }}>{c.task.name}</Typography>
            <Typography sx={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{c.start}{c.eind !== c.start ? ` – ${c.eind}` : ''}</Typography>
            <IconButton size="small" onClick={() => schuifConcept(i, -1)} sx={{ p: 0.25 }}><ChevronLeftIcon sx={{ fontSize: 15 }} /></IconButton>
            <IconButton size="small" onClick={() => schuifConcept(i, 1)} sx={{ p: 0.25 }}><ChevronRightIcon sx={{ fontSize: 15 }} /></IconButton>
            {c.task.deadline && String(c.eind) > String(c.task.deadline).slice(0, 10) && (
              <Typography sx={{ fontSize: 11 }} title={`Deadline ${String(c.task.deadline).slice(0, 10)} wordt overschreden`}>⚠️</Typography>
            )}
          </Box>
        ))}

        {bestaand.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 2, mb: 0.5 }}>
              Al ingepland bij {person?.firstName || person?.name} — schuif om ruimte te maken
            </Typography>
            {bestaand
              .filter((t) => (t.startDate && dagISOs.includes(String(t.startDate).slice(0, 10))) || bestaandeShift[t.id])
              .slice(0, 15)
              .map((t) => (
                <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25, opacity: 0.85 }}>
                  <Typography noWrap sx={{ fontSize: 12, flex: 1, minWidth: 0, color: '#475569' }}>{t.name}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {String(t.startDate).slice(0, 10)}{t.endDate && t.endDate !== t.startDate ? ` – ${String(t.endDate).slice(0, 10)}` : ''}
                    {bestaandeShift[t.id] ? ' (aangepast)' : ''}
                  </Typography>
                  <IconButton size="small" onClick={() => schuifBestaand(t, -1)} sx={{ p: 0.25 }}><ChevronLeftIcon sx={{ fontSize: 14 }} /></IconButton>
                  <IconButton size="small" onClick={() => schuifBestaand(t, 1)} sx={{ p: 0.25 }}><ChevronRightIcon sx={{ fontSize: 14 }} /></IconButton>
                </Box>
              ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" onClick={toepassen} disabled={busy || !concept.length}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {busy ? 'Bezig…' : `Plan ${concept.length} taken in`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function takenVanDeliverable(unassignedTasks, dlvId) {
  const uit = [];
  const gezien = new Set();
  Object.values(unassignedTasks || {}).forEach((group) => {
    (group.tasks || []).forEach((t) => {
      if (t.deliverableId === dlvId && !gezien.has(t.id)) { gezien.add(t.id); uit.push(t); }
    });
  });
  return uit;
}

// ─── Uitgeklapte persoonsrij: bestaande taken verschuiven om ruimte te maken ──

function PersonTaskLane({ person, tasks, workingDays, dayWidth }) {
  const zichtbaar = useMemo(
    () => (tasks || []).filter((t) => t.startDate && t.type !== 'Afspraak'),
    [tasks]
  );
  const { geplaatst, laneCount } = useMemo(
    () => layoutTaakBalken(zichtbaar, workingDays),
    [zichtbaar, workingDays]
  );
  if (!zichtbaar.length) {
    return (
      <Box sx={{ display: 'flex', borderBottom: '1px solid #f1f5f9', bgcolor: '#fbfdff' }}>
        <Box sx={{ width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH, borderRight: '1px solid #e2e8f0' }} />
        <Typography variant="caption" color="text.disabled" sx={{ p: 1 }}>
          Geen ingeplande taken in dit venster.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e7ff', bgcolor: '#fbfdff' }}>
      <Box sx={{ width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH, p: 0.5, pl: 3, borderRight: '1px solid #e2e8f0' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          taken van {person.firstName || person.name} — sleep om te schuiven
        </Typography>
      </Box>
      <Box sx={{
        flex: 1, position: 'relative', minHeight: laneCount * BAR_HEIGHT + 8, py: 0.5,
        backgroundImage: `repeating-linear-gradient(to right, #e2e8f0 0 1px, transparent 1px ${dayWidth}px)`,
      }}>
        {geplaatst.map(({ task, kolom, lane }) => (
          <DraggableTaskBar
            key={task.id}
            task={{ ...task, bronPersoonId: person.id }}
            dayWidth={dayWidth}
            totalDays={workingDays.length}
            workingDays={workingDays}
            left={kolom * dayWidth}
            top={lane * BAR_HEIGHT + 4}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function PlannerTodo() {
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username || '';

  // Data state
  const [unassignedTasks, setUnassignedTasks] = useState({});
  const [persons, setPersons] = useState([]);
  const [calendarHours, setCalendarHours] = useState({});   // { email: { date: uren } } uit de echte agenda
  const { rules, reload: herlaadRegels } = useCapacityRules();  // capaciteitsregels (planner)
  const [regelsOpen, setRegelsOpen] = useState(false);
  const [existingTasks, setExistingTasks] = useState({});
  const [loading, setLoading] = useState(true);

  // UI state
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  // 1/2/4 weken: minder weken = bredere kolommen, beter sleepbaar (Joris, 1 sep).
  const [numWeken, setNumWeken] = useState(() => Number(localStorage.getItem('plannerWeken')) || 2);
  const kiesWeken = (n) => { if (n) { setNumWeken(n); localStorage.setItem('plannerWeken', String(n)); } };
  const dayWidth = DAY_WIDTH_BY_WEEKS[numWeken] || DAY_WIDTH;
  const [deliverableFilter, setDeliverableFilter] = useState([]);
  const [expandedPersons, setExpandedPersons] = useState({}); // personId -> true
  const [speeltuin, setSpeeltuin] = useState(null); // { personId, startDatum, deliverableName, taken }
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedFunctions, setExpandedFunctions] = useState({ Editor: true, Animator: true, Illustrator: true, Overig: true });
  const [functionFilter, setFunctionFilter] = useState([]);
  const [projectFilter, setProjectFilter] = useState([]); // Selected project IDs
  const [selectedPerson, setSelectedPerson] = useState(null);

  // Drag state
  const [activeTask, setActiveTask] = useState(null);
  const [overCell, setOverCell] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Generate working days for timeline
  const workingDays = useMemo(() => generateWorkingDays(weekStart, numWeken), [weekStart, numWeken]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Unassigned taken + personen PARALLEL (was sequentieel — merkbaar sneller).
      const [tasksData, personsData] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/planner-todo/unassigned`).then(r => leesJson(r, 'Ongeplande taken laden')),
        authFetch(`${BACKEND_URL}/Api/planner-todo/persons`).then(r => leesJson(r, 'Personen laden')),
      ]);
      setUnassignedTasks(tasksData.byProject || tasksData.byDeliverable || {});

      // Expand first few projects by default
      const projects = Object.keys(tasksData.byProject || tasksData.byDeliverable || {});
      const expanded = {};
      projects.slice(0, 5).forEach(k => { expanded[k] = true; });
      setExpandedProjects(expanded);

      setPersons(personsData.persons || []);

      // Fetch existing tasks for all persons
      if (personsData.persons?.length > 0) {
        const startStr = formatISO(weekStart);
        const endStr = formatISO(addDays(weekStart, 35));
        const personIds = personsData.persons.map(p => p.id);

        const params = new URLSearchParams();
        params.append('startDate', startStr);
        params.append('endDate', endStr);
        personIds.forEach(id => params.append('personIds[]', id));

        const existingRes = await authFetch(`${BACKEND_URL}/Api/person-tasks-range?${params}`);
        const existingData = await leesJson(existingRes, 'Ingeplande taken laden');
        setExistingTasks(existingData || {});

        // Echte meeting-uren uit de Outlook-agenda — op de ACHTERGROND (blokkeert het
        // laden van de planner niet; verfijnt de capaciteit zodra binnen).
        const emails = personsData.persons.map(p => p.email).filter(Boolean);
        if (emails.length) {
          authFetch(`${BACKEND_URL}/Api/planner-todo/calendar-hours`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ emails, startDate: startStr, endDate: endStr }),
          }).then(r => leesJson(r, 'Agenda-uren laden')).then(d => setCalendarHours(d.hours || {})).catch(e => meldFout(e, 'Agenda-uren laden'));
        }
      }

    } catch (error) {
      console.error('Error fetching planner data:', error);
      toast.error('Kon data niet laden');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  // "Ongedaan maken" na slepen/toewijzen: de vorige velden (uitvoerder + datums,
  // vastgelegd vóór de PATCH) terugzetten en de planner herladen.
  const herstelTaak = useCallback(async (taskId, vorige) => {
    const r = await authFetch(`${BACKEND_URL}/Api/tasks/${taskId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vorige),
    });
    if (!r.ok) throw new Error('Terugzetten mislukt');
    await fetchData();
  }, [fetchData]);

  // Group persons by function
  const personsByFunction = useMemo(() => {
    const groups = { Editor: [], Animator: [], Illustrator: [], Overig: [] };
    persons.forEach(person => {
      const group = getPersonFunctionGroup(person);
      if (groups[group]) {
        groups[group].push(person);
      } else {
        groups.Overig.push(person);
      }
    });
    // Filter by selected functions
    if (functionFilter.length > 0) {
      return Object.fromEntries(
        Object.entries(groups).filter(([key]) => functionFilter.includes(key) || key === 'Overig')
      );
    }
    return groups;
  }, [persons, functionFilter]);

  // Get unique projects from tasks with task count
  const uniqueProjects = useMemo(() => {
    const projectMap = {};
    Object.values(unassignedTasks).forEach(group => {
      const id = group.projectId;
      if (!projectMap[id]) {
        projectMap[id] = {
          id,
          name: group.projectName,
          taskCount: 0
        };
      }
      projectMap[id].taskCount += group.tasks?.length || 0;
    });
    // Sort by task count descending
    return Object.values(projectMap).sort((a, b) => b.taskCount - a.taskCount);
  }, [unassignedTasks]);

  // Unieke deliverables (binnen het projectfilter) voor de tweede filterrij.
  const uniqueDeliverables = useMemo(() => {
    const kaart = {};
    Object.values(unassignedTasks).forEach((group) => {
      if (projectFilter.length && !projectFilter.includes(group.projectId)) return;
      (group.tasks || []).forEach((t) => {
        if (!t.deliverableId) return;
        if (!kaart[t.deliverableId]) {
          kaart[t.deliverableId] = { id: t.deliverableId, name: t.deliverableName || 'Deliverable', taskCount: 0 };
        }
        kaart[t.deliverableId].taskCount += 1;
      });
    });
    return Object.values(kaart).sort((a, b) => b.taskCount - a.taskCount);
  }, [unassignedTasks, projectFilter]);

  // Filter tasks by selected projects + deliverables
  const filteredTasks = useMemo(() => {
    let entries = Object.entries(unassignedTasks);
    if (projectFilter.length) entries = entries.filter(([, group]) => projectFilter.includes(group.projectId));
    if (deliverableFilter.length) {
      entries = entries
        .map(([key, group]) => [key, { ...group, tasks: (group.tasks || []).filter((t) => deliverableFilter.includes(t.deliverableId)) }])
        .filter(([, group]) => group.tasks.length > 0);
    }
    return Object.fromEntries(entries);
  }, [unassignedTasks, projectFilter, deliverableFilter]);

  // Total unassigned count
  const totalUnassigned = useMemo(() =>
    Object.values(unassignedTasks).reduce((sum, g) => sum + (g.tasks?.length || 0), 0),
    [unassignedTasks]
  );

  // Toggle project filter
  const toggleProjectFilter = (projectId) => {
    setProjectFilter(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  // Drag handlers
  const handleDragStart = (event) => {
    const { active } = event;
    const taskData = active.data.current;
    if (taskData?.type === 'task-bar') {
      setActiveTask(taskData.task);
    }
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (over?.data?.current?.type === 'capacity-cell') {
      setOverCell({
        personId: over.data.current.personId,
        date: over.data.current.date
      });
    } else {
      setOverCell(null);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTask(null);
    setOverCell(null);

    if (!over || !active) return;

    const taskData = active.data.current;
    const dropData = over.data.current;

    // Hele deliverable op een persoon: open de speeltuin-popup (Joris, 1 sep).
    if (taskData?.type === 'deliverable' && dropData?.type === 'capacity-cell') {
      const taken = taskData.tasks || [];
      if (!taken.length) return;
      setSpeeltuin({
        personId: dropData.personId,
        startDatum: dropData.date,
        deliverableName: taskData.deliverable?.name || 'Deliverable',
        taken,
      });
      return;
    }

    if (taskData?.type !== 'task-bar' || dropData?.type !== 'capacity-cell') return;

    const task = taskData.task;
    const { personId, date } = dropData;
    const person = persons.find(p => p.id === personId);

    // Calculate end date based on hours. De gebruiker kiest hier zelf de dag, dus
    // we verschuiven niet — bij een deadline-overschrijding waarschuwen we alleen.
    const taskDays = werkdagenVoor(task);
    const startDate = new Date(date);
    const endDate = eindDatumVoor(startDate, taskDays);
    const deadline = task.deadline ? startOfDay(new Date(task.deadline)) : null;
    const naDeadline = deadline && !isNaN(deadline) && endDate > deadline;

    // Bestaande taak (uit een uitgeklapte persoonsrij): verschuiven of naar een
    // andere collega — geen unassigned-administratie nodig (Joris, 1 sep).
    if (task.bronPersoonId) {
      const bron = task.bronPersoonId;
      // Vorige waarden vastleggen vóór de PATCH — voor "Ongedaan maken".
      const vorige = {
        ...(bron !== personId ? { Assignee: [bron] } : {}),
        StartDateUitvoerder: task.startDate || null,
        EndDateUitvoerder: task.endDate || null,
      };
      setExistingTasks(prev => {
        const upd = { ...prev, [bron]: (prev[bron] || []).filter(t => t.id !== task.id) };
        upd[personId] = [
          ...(upd[personId] || []),
          { ...task, bronPersoonId: undefined, startDate: date, endDate: formatISO(endDate) },
        ];
        return upd;
      });
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(bron !== personId ? { Assignee: [personId] } : {}),
            StartDateUitvoerder: date,
            EndDateUitvoerder: formatISO(endDate),
          }),
        });
        if (!res.ok) throw new Error('Update failed');
        const naam = person?.firstName || person?.name;
        biedOngedaanAan(bron === personId
          ? `Taak verschoven naar ${date}`
          : `Taak verplaatst naar ${naam} (${date})`, () => herstelTaak(task.id, vorige));
      } catch {
        toast.error('Kon taak niet verplaatsen');
        fetchData();
      }
      return;
    }

    // Voor "Ongedaan maken": terug naar de nog-in-te-plannen-lijst. De vorige
    // uitvoerder zit niet in de payload, dus die wordt leeggemaakt.
    const vorige = {
      Assignee: [],
      StartDateUitvoerder: task.startDate || null,
      EndDateUitvoerder: task.endDate || null,
      NogInTePlannen: true,
    };

    // Optimistic update: remove from unassigned
    setUnassignedTasks(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        updated[key] = {
          ...updated[key],
          tasks: updated[key].tasks.filter(t => t.id !== task.id)
        };
        if (updated[key].tasks.length === 0) delete updated[key];
      });
      return updated;
    });

    // Add to existing tasks
    setExistingTasks(prev => ({
      ...prev,
      [personId]: [
        ...(prev[personId] || []),
        { id: task.id, name: task.name, type: task.type, hours: task.hours, startDate: date, endDate: formatISO(endDate) }
      ]
    }));

    // Save to Airtable
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Assignee: [personId],
          StartDateUitvoerder: date,
          EndDateUitvoerder: formatISO(endDate),
          NogInTePlannen: false
        })
      });

      if (!res.ok) throw new Error('Update failed');
      const naam = person?.firstName || person?.name;
      biedOngedaanAan(naDeadline
        ? `Taak toegewezen aan ${naam} — loopt tot ${formatISO(endDate)}, ⚠️ ná de deadline (${formatISO(deadline)})`
        : `Taak toegewezen aan ${naam}`, () => herstelTaak(task.id, vorige));
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error('Kon taak niet toewijzen');
      fetchData();
    }
  };

  // Quick assign with selected person
  const handleQuickAssign = async (task) => {
    if (!selectedPerson) return;

    const person = persons.find(p => p.id === selectedPerson);
    // Plan in de week die de gebruiker bekijkt (niet altijd "vandaag") en houd
    // de deadline van de taak aan.
    const { start, eind, waarschuwing, verschoven } = planTaak(task, weekStart);
    const startISO = formatISO(start);
    const endISO = formatISO(eind);
    // Voor "Ongedaan maken": terug naar de nog-in-te-plannen-lijst.
    const vorige = {
      Assignee: [],
      StartDateUitvoerder: task.startDate || null,
      EndDateUitvoerder: task.endDate || null,
      NogInTePlannen: true,
    };

    // Optimistic update
    setUnassignedTasks(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        updated[key] = { ...updated[key], tasks: updated[key].tasks.filter(t => t.id !== task.id) };
        if (updated[key].tasks.length === 0) delete updated[key];
      });
      return updated;
    });

    setExistingTasks(prev => ({
      ...prev,
      [selectedPerson]: [
        ...(prev[selectedPerson] || []),
        { id: task.id, name: task.name, type: task.type, hours: task.hours, startDate: startISO, endDate: endISO }
      ]
    }));

    try {
      const res = await authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Assignee: [selectedPerson],
          StartDateUitvoerder: startISO,
          EndDateUitvoerder: endISO,
          NogInTePlannen: false
        })
      });

      if (!res.ok) throw new Error('Update failed');
      const naam = person?.firstName || person?.name;
      const melding = waarschuwing
        ? `Taak toegewezen aan ${naam} (${startISO}) — ⚠️ ${waarschuwing}`
        : verschoven
          ? `Taak toegewezen aan ${naam} — naar voren gehaald (${startISO}) om de deadline te halen`
          : `Taak toegewezen aan ${naam} (${startISO})`;
      biedOngedaanAan(melding, () => herstelTaak(task.id, vorige));
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error('Kon taak niet toewijzen');
      fetchData();
    }
  };

  // Week navigation
  // Weeknavigatie (vorige/volgende/vandaag + datumprikker) zit in <WeekSelector>.

  // Function filter handler
  const handleFunctionFilter = (event, newFilters) => {
    setFunctionFilter(newFilters || []);
  };

  // Selected person name
  const selectedPersonName = useMemo(() => {
    if (!selectedPerson) return '';
    const person = persons.find(p => p.id === selectedPerson);
    return person?.firstName || person?.name?.split(' ')[0] || '';
  }, [selectedPerson, persons]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ pb: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: 'white' }}>
          <PageHeader
            icon="assignment_ind"
            title="Planner To-Do"
            subtitle={`${totalUnassigned} taken in ${uniqueProjects.length} projecten`}
            rightActions={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {/* Function filter */}
                <ToggleButtonGroup
                  value={functionFilter}
                  onChange={handleFunctionFilter}
                  size="small"
                  sx={{ mr: 2 }}
                >
                  {FUNCTION_GROUPS.map(g => (
                    <ToggleButton key={g.key} value={g.key} sx={{ py: 0.5, px: 1.5, textTransform: 'none', fontSize: 12 }}>
                      {g.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {/* Week navigation — datumprikker zit in <WeekSelector> */}
                <WeekSelector weekStart={weekStart} onChange={setWeekStart} />
                <ToggleButtonGroup size="small" exclusive value={numWeken} onChange={(e, v) => kiesWeken(v)}>
                  {[1, 2, 4].map((n) => (
                    <ToggleButton key={n} value={n} sx={{ px: 1, py: 0.25, fontSize: '0.72rem', fontWeight: 700 }}>
                      {n}w
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Button
                  size="small" startIcon={<HelpIcon />} onClick={() => setRegelsOpen(true)}
                  sx={{ textTransform: 'none', fontWeight: 600, color: '#526075' }}
                >
                  Zo rekenen we dit uit
                </Button>
                <IconButton onClick={fetchData} size="small"><RefreshIcon /></IconButton>
              </Box>
            )}
          />
          {/* Taken die al op Afgerond staan en daardoor onzichtbaar zijn (9 okt) — alleen als er zijn */}
          <AfgerondSignaal sx={{ mt: 0.5 }} />

          {/* Project filter chips */}
          {uniqueProjects.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Projecten:
              </Typography>
              {projectFilter.length > 0 && (
                <Chip
                  label="Alles"
                  size="small"
                  variant="outlined"
                  onClick={() => setProjectFilter([])}
                  sx={{ height: 24, fontSize: 11 }}
                />
              )}
              {uniqueProjects.map(proj => {
                const isSelected = projectFilter.includes(proj.id);
                return (
                  <Chip
                    key={proj.id}
                    label={`${proj.name} (${proj.taskCount})`}
                    size="small"
                    onClick={() => toggleProjectFilter(proj.id)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{
                      height: 24,
                      fontSize: 11,
                      maxWidth: 200,
                      '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                    }}
                  />
                );
              })}
            </Box>
          )}
          {uniqueDeliverables.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Deliverables:
              </Typography>
              {uniqueDeliverables.slice(0, 24).map((dlv) => (
                <DraggableDeliverableChip
                  key={dlv.id}
                  dlv={dlv}
                  selected={deliverableFilter.includes(dlv.id)}
                  tasks={takenVanDeliverable(unassignedTasks, dlv.id)}
                  onClick={() => setDeliverableFilter((prev) => (
                    prev.includes(dlv.id) ? prev.filter((id) => id !== dlv.id) : [...prev, dlv.id]))}
                />
              ))}
              <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                sleep een deliverable op een persoon om alles in te passen
              </Typography>
            </Box>
          )}
        </Box>

        {/* Main content - Tasks above, Capacity below */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* TASKS SECTION (sticky top) */}
          <Box sx={{
            maxHeight: '40%',
            minHeight: 150,
            overflow: 'auto',
            borderBottom: '2px solid #3b82f6',
            bgcolor: '#fff',
          }}>
            <TimelineHeader workingDays={workingDays} dayWidth={dayWidth} />

            {Object.entries(filteredTasks).length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {projectFilter.length > 0 ? 'Geen taken voor geselecteerde projecten' : 'Geen ongeplande taken'}
                </Typography>
              </Box>
            ) : (
              Object.entries(filteredTasks).map(([key, project]) => (
                <ProjectTaskGroup
                  dayWidth={dayWidth}
                  key={key}
                  project={project}
                  workingDays={workingDays}
                  expanded={expandedProjects[key] || false}
                  onToggle={() => setExpandedProjects(prev => ({ ...prev, [key]: !prev[key] }))}
                />
              ))
            )}
          </Box>

          {/* CAPACITY SECTION (scrollable) */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {/* Capacity header */}
            <Box sx={{
              display: 'flex',
              position: 'sticky',
              top: 0,
              zIndex: 5,
              bgcolor: '#f1f5f9',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <Box sx={{ width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH, p: 1, borderRight: '1px solid #e5e7eb' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">CAPACITEIT</Typography>
              </Box>
              <Box sx={{ display: 'flex' }}>
                {workingDays.map((day, idx) => {
                  const today = isToday(day.date);
                  return (
                    <Box
                      key={day.iso}
                      sx={{
                        width: dayWidth,
                        minWidth: dayWidth,
                        textAlign: 'center',
                        py: 0.25,
                        borderRight: '1px solid #e2e8f0',
                        borderLeft: day.isFirstOfMonth && idx > 0 ? '2px solid #3b82f6' : 'none',
                        bgcolor: today ? '#eef2ff' : 'transparent',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontSize: 9, color: today ? '#6366f1' : '#94a3b8' }}>
                        {day.dayName} {day.dayNum}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Function groups */}
            {Object.entries(personsByFunction).map(([groupName, groupPersons]) => {
              if (groupPersons.length === 0) return null;
              const groupLabel = FUNCTION_GROUPS.find(g => g.key === groupName)?.label || groupName;

              return (
                <Box key={groupName}>
                  <FunctionGroupHeader
                    name={groupLabel}
                    count={groupPersons.length}
                    expanded={expandedFunctions[groupName]}
                    onToggle={() => setExpandedFunctions(prev => ({ ...prev, [groupName]: !prev[groupName] }))}
                  />
                  <Collapse in={expandedFunctions[groupName]}>
                    {/* Geselecteerde collega bovenaan en de rest gedimd — anders kijk je je
                        blind op twintig rijen (wiki-feedback Planningsgedeelte). */}
                    {[...groupPersons].sort((a, b) => (b.id === selectedPerson) - (a.id === selectedPerson)).map(person => (
                      <React.Fragment key={person.id}>
                        <CompactPersonRow
                          person={person}
                          workingDays={workingDays}
                          existingTasks={existingTasks[person.id]}
                          calHours={calendarHours[person.email] || {}}
                          rules={rules}
                          overCell={overCell}
                          selected={selectedPerson === person.id}
                          gedimd={!!selectedPerson && selectedPerson !== person.id}
                          onSelect={() => setSelectedPerson(prev => prev === person.id ? null : person.id)}
                          dayWidth={dayWidth}
                          expandedRow={!!expandedPersons[person.id]}
                          onToggleExpand={() => setExpandedPersons((prev) => ({ ...prev, [person.id]: !prev[person.id] }))}
                        />
                        {expandedPersons[person.id] && (
                          <PersonTaskLane person={person} tasks={existingTasks[person.id]}
                            workingDays={workingDays} dayWidth={dayWidth} />
                        )}
                      </React.Fragment>
                    ))}
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Selected person indicator */}
        {selectedPerson && (
          <Box sx={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          }}>
            <Chip
              icon={<PersonIcon />}
              label={`Toewijzen aan ${selectedPersonName} - klik op een taak`}
              color="primary"
              onDelete={() => setSelectedPerson(null)}
              sx={{ py: 2, px: 1, fontSize: 13, fontWeight: 500 }}
            />
          </Box>
        )}
      </Box>

      {/* Drag overlay */}
      <DragOverlay>
        {activeTask && (
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: TYPE_COLORS[activeTask.type] || '#6b7280',
              color: '#fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {activeTask.name} ({activeTask.hours}u)
          </Box>
        )}
      </DragOverlay>

      {/* Uitleg + (voor de planner) de instelbare rekenregels */}
      <Dialog open={regelsOpen} onClose={() => setRegelsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Capaciteitsregels</DialogTitle>
        <DialogContent dividers>
          <CapaciteitsRegels onSaved={herlaadRegels} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegelsOpen(false)} sx={{ textTransform: 'none' }}>Sluiten</Button>
        </DialogActions>
      </Dialog>
      {speeltuin && (
        <SpeeltuinDialog
          info={speeltuin}
          persons={persons}
          existingTasks={existingTasks}
          calendarHours={calendarHours}
          rules={rules}
          onClose={() => setSpeeltuin(null)}
          onKlaar={() => { setSpeeltuin(null); fetchData(); }}
        />
      )}
    </DndContext>
  );
}
